import { filterOptionsResponseSchema, leadCatalogResponseSchema, type FilterCondition, type FilterOptionsResponse, type LeadCatalogResponse } from "@gioia365/contracts";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { apiRequest } from "../../api/http";
import type { Translate } from "../../i18n/locale";
import { commercialViewOf, tabKey, type TabContext, type TabKey } from "../../session/tab-context";
import { knownLabels, optionLabel } from "./filter-text";

// Le letture del pannello «Filtri» (piano M3, D4). Le chiavi stanno sotto `["scheda", tabKey, "leads", …]`, come le
// altre letture dei Lead: il cambio di azienda le toglie e un cambio di «Chi vede i Lead» le rilegge. Portano la Vista
// Commerciale, perché i conteggi sono nel perimetro di chi guarda.

/** I campi che si cercano: le voci arrivano soltanto con almeno due caratteri, e al massimo trenta. */
export const PLACE_FILTER_FIELDS: ReadonlySet<string> = new Set(["city", "province"]);
/** I caratteri che servono per cercare città e province (`PLACE_SEARCH_MIN` del server). */
export const PLACE_SEARCH_MIN = 2;

/** I campi dell'azienda filtrabili e mostrabili (`GET /api/leads/catalog`), in una lettura. */
export function leadCatalogQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "leads", "catalog", commercialViewOf(tab)],
    queryFn: ({ signal }) => apiRequest("GET", "/api/leads/catalog", { schema: leadCatalogResponseSchema, tab, signal }),
    // Cambia soltanto quando si pubblicano i «Campi della scheda», che la rileggono (`usePublishLeadFields`).
    staleTime: 5 * 60_000,
  });
}

// I nomi delle voci lette (decisione P20): ogni lettura delle voci di un campo li ricorda per la scheda, finché la pagina
// resta aperta, anche quando la cache delle query li toglie. Così un filtro scelto si legge sempre con i suoi nomi, mai
// con un codice ISTAT o «1 scelto», anche quando arriva da un link o da una Smart List. Si tiene l'etichetta del server
// com'è (vuota dove la scrive l'interfaccia, nella lingua del momento). Una memoria per cache, cioè per scheda del
// browser, e dentro per scheda dell'azienda: due aziende non si mescolano.
const labelMemories = new WeakMap<QueryClient, Map<TabKey, Map<string, Map<string, string>>>>();

/** Ricorda i nomi delle voci lette per un campo. */
export function rememberFilterLabels(client: QueryClient, tab: TabContext, field: string, options: FilterOptionsResponse["options"]): void {
  let byTab = labelMemories.get(client);
  if (!byTab) {
    byTab = new Map();
    labelMemories.set(client, byTab);
  }
  const key = tabKey(tab);
  let byField = byTab.get(key);
  if (!byField) {
    byField = new Map();
    byTab.set(key, byField);
  }
  let labels = byField.get(field);
  if (!labels) {
    labels = new Map();
    byField.set(field, labels);
  }
  for (const option of options) labels.set(option.value, option.label);
}

const NO_LABELS: ReadonlyMap<string, string> = new Map();

/** I nomi ricordati delle voci di un campo (voce → etichetta del server, anche vuota). */
export function rememberedFilterLabels(client: QueryClient, tab: TabContext, field: string): ReadonlyMap<string, string> {
  return labelMemories.get(client)?.get(tabKey(tab))?.get(field) ?? NO_LABELS;
}

/** Il prefisso delle chiavi delle voci di un campo, per ogni testo cercato. */
export function filterOptionsKeyPrefix(tab: TabContext, field: string) {
  return ["scheda", tabKey(tab), "leads", "filter-options", commercialViewOf(tab), field] as const;
}

/** Una lettura delle voci di un campo: per testo cercato (`q`, vuoto per l'elenco intero) o per codici (`values`). */
function optionsQueryOf(tab: TabContext, field: string, by: "q" | "values", text: string) {
  return queryOptions({
    // `by` tiene distinte la ricerca per nome e quella per codice, anche con lo stesso testo.
    queryKey: [...filterOptionsKeyPrefix(tab, field), by, text] as const,
    queryFn: async ({ signal, client }) => {
      const params = new URLSearchParams({ field });
      if (text !== "") params.set(by, text);
      const body = await apiRequest("GET", `/api/leads/filter-options?${params.toString()}`, { schema: filterOptionsResponseSchema, tab, signal });
      rememberFilterLabels(client, tab, field, body.options);
      return body;
    },
    staleTime: 60_000,
  });
}

/**
 * Le voci di un filtro a scelta con quanti Lead visibili le hanno (`GET /api/leads/filter-options`): si leggono quando
 * si apre la riga del filtro, non all'apertura del pannello. `q` è il testo cercato, per città e province.
 */
export function filterOptionsQuery(tab: TabContext, field: string, q = "") {
  return optionsQueryOf(tab, field, "q", q);
}

/**
 * Le voci di città o province scelte, chieste per codice (`values=`, al massimo 200 come le voci di un filtro): il nome
 * di un filtro arrivato con l'indirizzo, che la ricerca per nome non saprebbe trovare.
 */
export function filterLabelsQuery(tab: TabContext, field: string, values: readonly string[]) {
  return optionsQueryOf(tab, field, "values", [...new Set(values)].sort().join(","));
}

/**
 * I campi i cui nomi delle voci arrivano dall'elenco intero delle voci; città e province si chiedono per codice. Gli
 * altri li conosce l'interfaccia (esiti, nazioni, regioni) o il catalogo dei campi dell'azienda.
 */
const SERVER_LABEL_FIELDS: ReadonlySet<string> = new Set(["owner", "status", "tags"]);

/**
 * Le letture dei nomi delle voci scelte che la scheda non ricorda ancora (decisione P20): l'elenco intero delle voci per
 * owner, status e tag, i soli codici scelti per città e province. Da passare a `useQueries`: ogni lettura li ricorda
 * (`rememberFilterLabels`), e chi le usa si ridisegna quando arrivano. Le usano i filtri attivi e le pagine che dicono
 * dei filtri in parole (la Smart List, il suo storico).
 */
export function filterLabelQueries(client: QueryClient, tab: TabContext, conditions: readonly FilterCondition[]) {
  return conditions.flatMap((condition) => {
    if (condition.kind !== "choice" && condition.kind !== "tags") return [];
    const remembered = rememberedFilterLabels(client, tab, condition.field);
    const missing = condition.values.filter((value) => !remembered.has(value));
    if (missing.length === 0) return [];
    if (PLACE_FILTER_FIELDS.has(condition.field)) return [filterLabelsQuery(tab, condition.field, missing)];
    return SERVER_LABEL_FIELDS.has(condition.field) ? [filterOptionsQuery(tab, condition.field)] : [];
  });
}

/**
 * I nomi delle voci di un campo: quelli che l'interfaccia conosce (per le voci `values`), poi quelli ricordati per la
 * scheda, con le parole dell'interfaccia dove il server lascia l'etichetta vuota.
 */
export function filterValueLabels(
  t: Translate,
  client: QueryClient,
  tab: TabContext,
  ref: string,
  values: readonly string[],
  catalog: LeadCatalogResponse | undefined,
): Map<string, string> {
  const labels = knownLabels(t, ref, values, catalog);
  for (const [value, label] of rememberedFilterLabels(client, tab, ref)) {
    if (!labels.has(value)) labels.set(value, optionLabel(t, ref, { value, label }));
  }
  return labels;
}
